import { useContext, useEffect, useMemo, useState } from "react"

import styled from "styled-components"

import { Lightmode } from "components/Providers"
import Button from "components/buttons/Button"
import HeaderPopoutBox from "components/header/HeaderPopoutBox"
import { useI18next } from "gatsby-plugin-react-i18next"
import { ReactComponent as Chevron } from "images/global/chevron-right.svg"
import logoCompany from "images/global/logo-company.svg"
import logoDevelopers from "images/global/logo-developers.svg"
import logoPlatform from "images/global/logo-platform.svg"
import logoProducts from "images/global/logo-products.svg"
import logoSolutions from "images/global/logo-solutions.svg"
import logoDeposits from 'images/global/logo-deposits.svg';
import logoChannels from 'images/global/logo-channels.svg'
import colors from "styles/colors"
import easing from "styles/easing"
import media from "styles/media"
import text from "styles/text"
import { LocaleData } from "types/gatsby-alias"
import { Opacity } from "utils/functions"
import useGetStaticTranslations from "utils/hooks/useGetStaticTranslations"
import links from "utils/links"
import { routesForHeader, checkedLocaleLink } from "utils/routes"

const ArrayOfPagesToAlwaysDisable = [
  "#blog-or-news-post",
  "#landing_page",
  "#event-post",
  "#landing_nav",
  "#contact",
]

export default function Header({languageOverride}   : {languageOverride?: string }) {
  const [burgerMode, setBurgerMode] = useState("none") // 3 possible states: none, closed, open
  const [open, setOpen] = useState(false)
  const [keepOpen, setKeepOpen] = useState(false)
  const [tab, setTab] = useState(0)
  const { lightmode, setLightmode } = useContext(Lightmode)
  const [visible, setVisible] = useState(true)
  const { language } = useI18next()
  
const localeDataJS: LocaleData = useGetStaticTranslations("menu", languageOverride )

  useEffect(() => {
    ArrayOfPagesToAlwaysDisable.forEach(pageId => {
      const page = document.querySelector(pageId)
      if (page) {
        setVisible(false)
      }
    })
  }, [])

  useEffect(() => {
    ArrayOfPagesToAlwaysDisable.forEach(pageId => {
      const page = document.querySelector(pageId)
      if (page) {
        setVisible(false)
      }
    })
  }, [setLightmode])

  useEffect(() => {
    setOpen(false)
  }, [burgerMode])

  const categories = useMemo(() => {
    const color = lightmode ? colors.blue800 : colors.white

    return data.map((el, i) => {
      const colorWithOpacity = tab === i ? color : Opacity(color, 0.5)

      return (
        <Category
          key={el.title}
          color={open ? colorWithOpacity : color}
          onClick={() => {
            setKeepOpen(true)
          }}
          onMouseEnter={() => {
            setTab(i)
            setOpen(true)
            setKeepOpen(false)
          }}
        >
          <ChevronWrapper
            tab={tab}
            i={i}
            open={open}
            color={open ? colorWithOpacity : color}
          >
            <Chevron />
          </ChevronWrapper>
          <CategoryText>{localeDataJS[el.title]}</CategoryText>
        </Category>
      )
    })
  }, [tab, open, lightmode, localeDataJS])

  return (
    <>
      <CloseBox
        open={open}
        onMouseEnter={() => !keepOpen && setOpen(false)}
        onClick={() => {
          setKeepOpen(false)
          setOpen(false)
        }}
        onKeyUp={() => {
          setKeepOpen(false)
          setOpen(false)
        }}
        role="presentation"
      />
      <Wrapper visible={visible}>
        <Center>
          <Nav
            id="nav"
            onClick={e => e.stopPropagation()}
            onKeyUp={e => e.stopPropagation()}
            role="presentation"
          >
            <Categories>
              {categories}{" "}
              <HeaderPopoutBox
                translation={localeDataJS}
                languageOverride={languageOverride}
                tab={data[tab]}
                index={tab}
                open={open}
              />
            </Categories>

            <Button
              lightMode={lightmode}
              onClick={() => {
                window.open(checkedLocaleLink(links.login, language), "_blank")
                setBurgerMode(burgerMode !== "none" ? "none" : "closed")
              }}
            >
              {localeDataJS.Login}
            </Button>
          </Nav>
        </Center>
      </Wrapper>
    </>
  )
}

const Category = styled.button`
  width: 100%;
  margin-bottom: 2.108vw;
  cursor: pointer;
  transition-timing-function: ${easing.button};
  transition-duration: 0.3s;
  color: ${props => props.color};
  border: none;
  background-color: transparent;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 0;
  height: 1.265vw;

  ${media.fullWidth} {
    margin-bottom: 35px;
    height: 21px;
  }
  ${media.tablet} {
    margin-bottom: 3.418vw;
    height: 2.051vw;
  }
`

const ChevronWrapper = styled.div<{ tab: number; open: boolean; i: number }>`
  display: flex;
  ${media.fullWidth} {
    width: 15px;
    height: 15px;
  }
  ${media.tablet} {
    width: 1.465vw;
    height: 1.465vw;
  }
  width: 0.904vw;
  height: 0.904vw;
  transition-timing-function: ${easing.button};
  transition-duration: 0.3s;
  transform: ${props =>
    props.i === props.tab && props.open ? `rotate(180deg);` : `rotate(0deg);`};
  path {
    transition-timing-function: ${easing.button};
    transition-duration: 0.3s;
    stroke: ${props => props.color};
  }
`

const CategoryText = styled.p`
  ${media.fullWidth} {
    margin-left: 10px;
    line-height: 20.8px;
  }
  ${media.tablet} {
    margin-left: 0.977vw;
    line-height: 2.031vw;
  }
  margin-left: 0.602vw;
  line-height: 1.253vw;
  text-transform: capitalize;
  ${text.buttonR}
`

const CloseBox = styled.div<{ open: boolean }>`
  display: ${props => (props.open ? "flex" : "none")};
  position: absolute;
  top: 0;
  width: 100%;
  height: 150vh;
  z-index: 19;
`

const Wrapper = styled.nav<{ visible?: boolean }>`
  width: 100%;
  display: ${props => (props.visible ? "flex" : "none")};
  justify-content: center;
  position: absolute;
  z-index: 20;
`

const Center = styled.div`
  width: 100%;
  max-width: 1660px;
  position: relative;
`

const Nav = styled.div`
  ${media.fullWidth} {
    top: 90px;
    right: 126px;
  }
  ${media.tablet} {
    top: 7.324vw;
    right: 3.077vw;
  }
  ${media.mobile} {
    display: none;
  }
  display: flex;
  flex-direction: column;
  position: absolute;
  top: 5.422vw;
  right: 7.59vw;

  align-items: left;
  justify-content: center;
  z-index: 5;
`

export type Section = {
  title: string;
  left?: boolean;
  links: {
    title: string;
    url: string;
  }[];
};
const Categories = styled.div``
export const data:
{
  title:"Deposits" | "Engagement Channels" | "Payments & Cards" | "Lending" | "Risk" | "Platform" | "Company"
  description:string
  image:string
  links:Section[]
}[] = [
  {
    title: "Deposits",
    description:"Solutions for modernizing deposits, back-end core infrastructure and cloud-native migrations.",
    links: routesForHeader.deposits,
    image: logoDeposits,
  },
  {
    title: "Engagement Channels",
    description:
      "Solutions to modernize digital banking, featuring AI-powered tools, self-service options, and digital engagement channels for seamless customer interactions.",
    links: routesForHeader.channels,
    image: logoChannels,
  },
  {
    title: "Payments & Cards",
    description:
      "Comprehensive products and services for card issuing, payment hub and program management. Including customer support and dispute resolution.",
    links: routesForHeader.payments_and_cards,
    image: logoSolutions,
  },
  {
    title: "Lending",
    description:"Integrated support for card issuing, payment processing, credit, analytics and risk so your program can reach its goals.",
    links: routesForHeader.lending,
    image: logoProducts,
  },
  {
    title: "Risk",
    description:
      "Advanced tools for payment risk management, instant verification, and comprehensive fraud operations support.",
    links: routesForHeader.risk,
    image: logoDevelopers,
  },
  {
    title: "Platform",
    description:
      "A cloud-based, developer-friendly, and regulation-ready solution with open APIs, sandbox testing, and trusted partnerships to serve financial institutions, fintechs, and brands.",
    links: routesForHeader.platforms,
    image: logoPlatform,
  },
  {
    title: "Company",
    description:
      "For decades, we've pushed the boundaries of what is possible in financial services and payments.",
    links: routesForHeader.company,
    image: logoCompany,
  },
]
